import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import {
	MenuListItem,
	Settings as SettingsTrigger,
} from '@atlaskit/navigation-system/top-nav-items';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';
import { type PopupTriggerProps, PopupTrigger } from '@atlassian/entry-points/popup-trigger';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type { SettingsProps } from '../../common/types';
import { messages } from './messages';

type PopupTriggerRenderProps = Parameters<PopupTriggerProps<AnyEntryPoint>['trigger']>[0];

/**
 * __Settings__
 *
 * The settings item in the top navigation.
 *
 * This is the platform default handed to `SettingsSlot`. It demonstrates the
 * division the slot API draws between the two kinds of content in the top nav:
 *
 *  - The trigger is initially visible, so it is a plain component rendered
 *    eagerly, with a translated label the app no longer has to supply.
 *  - The menu is only visible after the user opens it, so it comes from an
 *    entry point. Its code and data are not in the initial bundle, and
 *    `PopupTrigger` preloads them from the trigger on hover, focus and
 *    pointer-down, so the content is usually ready by the time the click lands.
 *
 * The app supplies the entry point and, when needed, presentation/dismissal
 * options through `popupProps` — no popup state, loading/error states, preload
 * plumbing or label translation. That is the integration code the slot
 * mechanism is meant to delete.
 */
export function Settings<TEntryPoint extends AnyEntryPoint>({
	entryPoint,
	entryPointParams,
	entryPointProps,
	errorFallback,
	fallback,
	interactionName,
	isSelected,
	label,
	onClick,
	testId,
	popupProps,
}: SettingsProps<TEntryPoint>): JSX.Element {
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const resolvedLabel = label ?? formatMessage(messages.settings);

	// Fired before the click is handed on, so the event is recorded even when the
	// consumer's handler navigates away.
	const fireSettingsClicked = useCallback(
		(slotType: 'button' | 'dropdown') => {
			fireEvent('ui.topNav.clicked.settings', { slotType });
		},
		[fireEvent],
	);

	const renderTrigger = useCallback(
		// Popup attaches its activation behavior through the trigger ref, so this
		// handler only records analytics and hands the click on to the app.
		(triggerProps: PopupTriggerRenderProps) => (
			<SettingsTrigger
				{...triggerProps}
				label={resolvedLabel}
				testId={testId}
				interactionName={interactionName}
				// Defaults to the popup's own open state, which the popup already
				// reports for assistive technology, so the trigger reflects its
				// menu without anything else having to track it. An app that does
				// track selection itself — because its menu can be "current" while
				// closed, say — can override it.
				isSelected={isSelected ?? triggerProps['aria-expanded']}
				onClick={(event) => {
					fireSettingsClicked('dropdown');
					onClick?.(event);
				}}
				// The trigger is wrapped in the `MenuListItem` below, so it must not
				// render a second `listitem` role of its own.
				isListItem={false}
			/>
		),
		[fireSettingsClicked, interactionName, isSelected, onClick, resolvedLabel, testId],
	);

	// Some apps navigate to a settings page rather than opening a menu. Without
	// an entry point there is no content to load, so the trigger is rendered on
	// its own and the app handles activation.
	if (!entryPoint) {
		return (
			<SettingsTrigger
				label={resolvedLabel}
				interactionName={interactionName}
				isSelected={isSelected}
				isListItem
				onClick={(event) => {
					fireSettingsClicked('button');
					onClick?.(event);
				}}
				testId={testId}
			/>
		);
	}

	return (
		<MenuListItem>
			<PopupTrigger
				placement="bottom-end"
				{...popupProps}
				entryPoint={entryPoint}
				entryPointParams={entryPointParams}
				entryPointProps={entryPointProps}
				errorFallback={errorFallback}
				fallback={fallback}
				trigger={renderTrigger}
			/>
		</MenuListItem>
	);
}
